'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  onDownloadEvidence: () => void;
}

const HeroSection = ({ onDownloadEvidence }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const trustIndicators = [
    { label: 'CDSCO Approved', value: '2023' },
    { label: 'Patient Outcome Improvement', value: '68%' },
    { label: 'Hospital Partners in India', value: '40+' },
  ];

  const contactLinks = [
    {
      icon: 'PhoneIcon',
      label: 'Call Us',
      value: '(+91) 9446065047',
      href: 'tel:+919446065047',
    },
    {
      icon: 'EnvelopeIcon',
      label: 'Email Us',
      value: 'contact@sensivisionhealth.com',
      href: 'mailto:contact@sensivisionhealth.com',
    },
  ];

  return (
    <section id="hero" className="relative bg-gradient-to-br from-primary via-neuro-primary to-primary/90 min-h-screen flex items-center pt-4">
      <div className="absolute inset-0 opacity-10 monitoring-grid">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        <div className="absolute inset-0 eeg-waveform-pattern" />
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-16 relative z-10 pr-0 pb-0">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-primary-foreground relative">
            <div className="inline-flex items-center space-x-2 bg-nicu-vital/20 text-success-foreground px-4 py-2 rounded-2xl mb-6 brain-activity-glow">
              <Icon name="ShieldCheckIcon" size={20} />
              <span className="text-sm font-semibold">ISO 13485 | CDSCO Approved</span>
            </div>

            <h1 className="lg:text-5xl font-bold font-heading mb-6 text-[24px]">
              World's First and Only Whole Body Cooling with Integrated Brain Monitoring
            </h1>

            <p className="text-xl lg:text-2xl mb-8 opacity-90 leading-relaxed">
              REVIVE redefines neuroprotective care for newborns with Birth Asphyxia—combining therapeutic hypothermia and continuous aEEG monitoring in one breakthrough device, making life-saving therapy accessible across more hospitals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              {contactLinks.map((contact, index) => (
                <a
                  key={index}
                  href={contact.href}
                  className="bg-accent text-accent-foreground px-8 py-4 rounded-2xl font-cta font-bold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250 ease-out flex items-center justify-center space-x-2"
                >
                  <Icon name={contact.icon as any} size={24} />
                  <span>{contact.label}: {contact.value}</span>
                </a>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-6">
              {trustIndicators.map((indicator, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold font-heading mb-1">{indicator.value}</div>
                  <div className="text-sm opacity-80">{indicator.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-card brain-activity-glow">
              <AppImage
                src="/assets/images/Revive_in_Hospital_5-1766903195945.jpeg"
                alt="REVIVE whole body cooling device by Sensivision Health Technologies Pvt. Ltd. in hospital NICU treating newborn with Hypoxic Ischemic Encephalopathy, showing touchscreen display with 36.2°C core body temperature monitoring and blue cooling mat"
                className="w-full h-[600px] object-cover"
                priority={true}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neuro-primary/60 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;